import type { PropsWithChildren, ReactNode } from "react";

import { graphql, useFragment } from "react-relay";

import { PageWithBreadCrumbs } from "app/components/layout/PageWithBreadCrumbs";
import { useSettingsBreadcrumbs } from "app/hooks/routing/useSettingsBreadcrumbs";

import { type AccessControlSettingsPage_query$key } from "./__generated__/AccessControlSettingsPage_query.graphql";

import type { BreadCrumbElement } from "app/components/BreadCrumbsHeader";

interface Props extends PropsWithChildren {
  queryRef: AccessControlSettingsPage_query$key;
  pageTitle?: string;
  /** Crumbs between the active settings tab and the current page (e.g. a parent entity). */
  additionalCrumbs?: BreadCrumbElement[];
  /** Page heading (H2) rendered above the content */
  heading?: string;
  /** Description rendered under the heading */
  subheading?: ReactNode;
  /** Constrain the paper's content to a fixed form width */
  isForm?: boolean;
  /** Whether to wrap the page content in a card-like Paper */
  withPaper?: boolean;
  /** Help shown in the side panel while `?help=true`. */
  helpContent?: ReactNode;
}

export function AccessControlSettingsPage({
  queryRef,
  pageTitle,
  additionalCrumbs,
  children,
  heading,
  subheading,
  isForm,
  withPaper,
  helpContent,
}: Props) {
  const data = useFragment(
    graphql`
      fragment AccessControlSettingsPage_query on Query {
        ...PageWithBreadCrumbs_query
      }
    `,
    queryRef,
  );

  const breadcrumbs = [
    ...useSettingsBreadcrumbs(),
    ...(additionalCrumbs ?? []),
  ];

  return (
    <PageWithBreadCrumbs
      breadcrumbs={breadcrumbs}
      defaultTitle="Settings"
      heading={heading}
      helpContent={helpContent}
      isForm={isForm}
      queryRef={data}
      subheading={subheading}
      title={pageTitle ?? "Access Control"}
      withPaper={withPaper}
    >
      {children}
    </PageWithBreadCrumbs>
  );
}
